
import React, { useState } from 'react';
import { PlayerProfile, IdolRank } from '../types';
import { RANKS, CITIES } from '../constants';
import { Building, DollarSign, Users, Briefcase, Award, Star, TrendingUp, Crown, ArrowUpCircle, MapPin } from 'lucide-react';
import { ExitButton } from './ExitButton';
import { Notification } from './NotificationSystem';

interface RecordLabelProps {
    player: PlayerProfile;
    onUpdatePlayer: (updates: Partial<PlayerProfile>) => void;
    onExit: () => void;
    onNotify: (message: string, type?: Notification['type']) => void;
}

export const RecordLabel: React.FC<RecordLabelProps> = ({ player, onUpdatePlayer, onExit, onNotify }) => {
    const [tab, setTab] = useState<'CONTRACTS' | 'PROMOTION'>('CONTRACTS');

    const multiplier = RANKS[player.rank]?.multiplier || 1;
    const nextRankId = player.rank + 1;
    const nextRankData = RANKS[nextRankId as IdolRank] || RANKS[IdolRank.TRAINEE];
    const isMaxRank = !RANKS[nextRankId as IdolRank];

    const promoteRank = () => {
        if (isMaxRank) return;

        if (player.fans < nextRankData.fanReq) {
            onNotify(`You need ${nextRankData.fanReq.toLocaleString()} Fans to challenge this exam!`, 'error');
            return;
        }
        if (player.cash < nextRankData.cashReq) {
            onNotify(`The promotion exam fee is $${nextRankData.cashReq.toLocaleString()}. You cannot afford it.`, 'error');
            return;
        }

        onUpdatePlayer({
            cash: player.cash - nextRankData.cashReq,
            rank: nextRankId as IdolRank,
            mysticWards: Math.min(99, player.mysticWards + 2) // Bonus wards for ranking up
        });
        onNotify(`CONGRATULATIONS! HUNTR/X has been promoted to ${nextRankData.label} Rank! +2 Wards acquired. New flight paths are now available.`, 'success');
    };

    return (
        <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-2 md:p-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-slate-950 to-slate-950"></div>
            <ExitButton onExit={onExit} />

            <div className="relative z-10 w-full max-w-5xl h-full md:max-h-full glass-panel p-6 md:p-10 rounded-lg shadow-2xl border border-slate-800 flex flex-col">
                <div className="flex items-center gap-6 mb-8 border-b border-slate-800 pb-8 shrink-0">
                    <div className="w-24 h-24 bg-blue-500/10 border border-blue-500/30 rounded-3xl flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.2)]">
                        <Building size={56} className="text-blue-400" />
                    </div>
                    <div>
                        <h2 className="text-4xl md:text-5xl font-heading text-blue-400 tracking-tight">STARLIGHT GLOBAL</h2>
                        <div className="flex items-center gap-2 text-slate-400 tracking-[0.3em] uppercase text-xs mt-2 font-bold">
                            <span>Talent Acquisition</span>
                            <span className="w-1 h-1 bg-slate-600 rounded-full"></span>
                            <span>Rank Management</span>
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 mb-6 shrink-0">
                    <button
                        onClick={() => setTab('CONTRACTS')}
                        className={`flex-1 py-3 rounded-lg font-heading tracking-widest transition-all
                    ${tab === 'CONTRACTS' ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}
                `}
                    >
                        MISSIONS & CONTRACTS
                    </button>
                    <button
                        onClick={() => setTab('PROMOTION')}
                        className={`flex-1 py-3 rounded-lg font-heading tracking-widest transition-all
                    ${tab === 'PROMOTION' ? 'bg-pink-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}
                `}
                    >
                        RANK PROMOTION
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto pr-4 custom-scrollbar relative">
                    {tab === 'CONTRACTS' && (
                        <div className="pb-8">
                            {player.activeContracts.length === 0 ? (
                                <div className="text-center py-12 text-slate-500 flex flex-col items-center gap-4">
                                    <Briefcase size={48} className="mx-auto" />
                                    <h3 className="text-lg font-bold">No Active Contracts</h3>
                                    <p className="max-w-sm">Wait for a message from Jinu to get new missions. Check the City Hub for a feathered messenger icon!</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {player.activeContracts.map(c => (
                                        <div key={c.id} className="bg-slate-900/60 border border-green-500/50 p-6 rounded-2xl">
                                            <h3 className="text-xl font-heading text-slate-100">{c.title}</h3>
                                            <p className="text-slate-400 text-sm mt-2 leading-relaxed h-12 overflow-hidden line-clamp-2">{c.description}</p>

                                            <div className="flex flex-col gap-3 mt-4 border-t border-slate-700 pt-4">
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-slate-500 font-bold uppercase">Payout</span>
                                                    <span className="font-mono text-green-400">+$ {((c?.reward || 0) * (RANKS[player.rank]?.multiplier || 1)).toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-slate-500 font-bold uppercase">Exposure</span>
                                                    <span className="font-mono text-pink-400">+{((c?.fanReward || 0) * (RANKS[player.rank]?.multiplier || 1)).toLocaleString()} Fans</span>
                                                </div>
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-slate-500 font-bold uppercase">Destination</span>
                                                    <div className="font-bold text-white flex items-center gap-2">
                                                        <MapPin size={14} /> {CITIES[c.destination]?.name || c.destination}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-4 text-center bg-green-500/10 px-4 py-2 text-green-400 text-xs font-bold rounded-full uppercase tracking-widest">
                                                Status: ACTIVE
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'PROMOTION' && (
                        <div className="flex flex-col items-center justify-center min-h-full pb-8">
                            {!isMaxRank ? (
                                <div className="w-full max-w-2xl bg-slate-900/80 border border-slate-700 p-8 rounded-2xl flex flex-col items-center text-center">
                                    <Crown size={64} className="text-pink-500 mb-6" />
                                    <h3 className="text-2xl font-heading text-slate-100 mb-2">PROMOTION EXAM: {nextRankData.label}</h3>
                                    <p className="text-slate-400 mb-8 max-w-md">
                                        Prove to Starlight Global that HUNTR/X is ready for the next tier of operations.
                                        Promotion unlocks new flight paths to dangerous but lucrative cities.
                                    </p>

                                    <div className="grid grid-cols-2 gap-8 w-full mb-8">
                                        <div className={`p-4 rounded-lg border ${player.fans >= nextRankData.fanReq ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
                                            <div className="text-xs uppercase font-bold tracking-widest mb-1 opacity-70">Fanbase Req</div>
                                            <div className={`text-xl font-bold ${player.fans >= nextRankData.fanReq ? 'text-green-400' : 'text-red-400'}`}>
                                                {player.fans.toLocaleString()} / {nextRankData.fanReq.toLocaleString()}
                                            </div>
                                        </div>
                                        <div className={`p-4 rounded-lg border ${player.cash >= nextRankData.cashReq ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
                                            <div className="text-xs uppercase font-bold tracking-widest mb-1 opacity-70">Exam Fee</div>
                                            <div className={`text-xl font-bold ${player.cash >= nextRankData.cashReq ? 'text-green-400' : 'text-red-400'}`}>
                                                ${nextRankData.cashReq.toLocaleString()}
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={promoteRank}
                                        disabled={player.fans < nextRankData.fanReq || player.cash < nextRankData.cashReq}
                                        className="w-full py-4 bg-pink-600 hover:bg-pink-500 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white font-heading text-xl rounded-lg shadow-[0_0_20px_rgba(236,72,153,0.4)] transition-all"
                                    >
                                        <div className="flex items-center justify-center gap-3">
                                            <ArrowUpCircle />
                                            <span>ATTEMPT PROMOTION EXAM</span>
                                        </div>
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center p-12 bg-slate-900/50 rounded-2xl border border-yellow-500/30 w-full max-w-2xl relative overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-to-t from-yellow-500/10 to-transparent"></div>
                                    <Crown size={80} className="text-yellow-400 mb-4 mx-auto animate-bounce" />
                                    <h2 className="text-4xl font-heading text-yellow-400 drop-shadow-md">MAXIMUM RANK ACHIEVED</h2>
                                    <div className="h-1 w-32 bg-yellow-500 mx-auto my-4 rounded-full"></div>
                                    <p className="text-2xl text-slate-100 font-bold mb-2">HUNTR/X has become a Global Legend.</p>
                                    <p className="text-slate-400 text-sm">The world is safe... for now. Keep performing to maintain the Honmoon barrier!</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none z-20"></div>
            </div>
        </div>
    );
};
